export const CURRENCY_LOCALE_MAP: Record<string, string> = {
  USD: 'en-US',
  EUR: 'de-DE',
  GBP: 'en-GB',
  JPY: 'ja-JP',
  CNY: 'zh-CN',
  CHF: 'de-CH',
  CAD: 'en-CA',
  AUD: 'en-AU',
  KRW: 'ko-KR',
  UAH: 'uk-UA',
  KZT: 'kk-KZ',
  AED: 'ar-AE',
  PLN: 'pl-PL',
  TRY: 'tr-TR',
  THB: 'th-TH',
  BYN: 'be-BY',
  RUB: 'ru-RU',
};

const DEFAULT_LOCALE = 'en-US';

/**
 * Дефолтная валюта (env или fallback).
 *
 * EUR — функциональная валюта компании (ADR-MONEY §7, решение владельца 02.08.2026:
 * регистрация в Финляндии, стартовый рынок — Франция). Применяется ТОЛЬКО как fallback,
 * когда валюта суммы не пришла с сервера и не выводится из страны/локали.
 */
export const DEFAULT_CURRENCY =
  process.env['REACT_APP_DEFAULT_CURRENCY'] ?? 'EUR';

/** Дефолтная страна (env или fallback). */
export const DEFAULT_COUNTRY =
  process.env['REACT_APP_DEFAULT_COUNTRY'] ?? 'RU';

/**
 * Resolves currency from a locale string (e.g. 'en-GB', 'ru', 'de').
 * Used when UI language is chosen by user so currency follows the selected locale.
 */
function currencyFromLocale(language: string): string {
  const lang = language.split('-')[0];
  const full = language.split('-').join('-');

  // North America
  if (full.startsWith('en-US')) return 'USD';
  if (full.startsWith('en-CA') || full.startsWith('fr-CA')) return 'CAD';

  // Europe
  if (full.startsWith('en-GB')) return 'GBP';
  if (full.startsWith('de-CH') || full.startsWith('fr-CH') ||
      full.startsWith('it-CH') || full.startsWith('rm-CH')) return 'CHF';

  // EU languages (by language code)
  const euLocales = ['de', 'fr', 'es', 'it', 'nl', 'pt', 'fi', 'sv', 'no', 'da',
    'pl', 'cs', 'sk', 'hu', 'ro', 'bg', 'hr', 'sl', 'et', 'lv', 'lt', 'el', 'mt', 'cy', 'ga', 'lb'];
  if (euLocales.some(loc => full.startsWith(loc))) return 'EUR';

  // Asia
  if (full.startsWith('zh')) return 'CNY';
  if (full.startsWith('ja')) return 'JPY';
  if (full.startsWith('ko')) return 'KRW';

  // Australia and Oceania
  if (full.startsWith('en-AU') || full.startsWith('en-NZ')) return 'AUD';

  // CIS
  const cisLocales = ['ru', 'kk', 'ky', 'uz', 'tg', 'be'];
  if (cisLocales.some(loc => lang === loc || full.startsWith(loc))) return 'RUB';

  // Ukraine, Caucasus, Moldova
  const euroOrientedLocales = ['uk', 'az', 'hy', 'ka', 'mo'];
  if (euroOrientedLocales.some(loc => lang === loc || full.startsWith(loc))) return 'EUR';

  // Middle East and Africa
  const usdLocales = ['ar', 'he', 'fa', 'tr'];
  if (usdLocales.some(loc => lang === loc || full.startsWith(loc))) return 'USD';

  return DEFAULT_CURRENCY;
}

/**
 * Валюта ВВОДА, подсказанная локалью: чем вероятнее всего оперирует человек,
 * который сейчас будет ВПИСЫВАТЬ сумму. Предзаполняет селектор валюты бюджета,
 * подсказывает диапазон в мастере события, задаёт валюту цены, которую поставщик
 * набирает своими руками.
 *
 * ЧЕМ ЭТА ФУНКЦИЯ НЕ ЯВЛЯЕТСЯ (EUR-5). Она НЕ говорит, в какой валюте выражена
 * сумма, ПРИШЕДШАЯ С СЕРВЕРА. Локаль зрителя не меняет деньги на счету: русский
 * администратор, открывший выручку в 1000 EUR, до правки видел «1 000 ₽» — не
 * пересчёт, а подмена подписи. Для серверных сумм есть ровно два верных источника:
 *   1. поле валюты в самом ответе (`event.currency`, `earning.currency`, …);
 *   2. `BASE_CURRENCY` / `formatBaseAmount` — когда контракт валюту не несёт,
 *      а сумма выражена в функциональной валюте платформы (ADR-MONEY §7).
 * Связка `formatCurrency(x, getDefaultCurrency())` запрещена правилом ESLint
 * `no-restricted-syntax` в `.eslintrc.cjs`; смешанный по валютам итог считает
 * `formatMixedTotal`.
 *
 * Supports: USD, EUR, GBP, RUB, CNY, JPY, CHF, CAD, AUD, KRW.
 */
export const getDefaultCurrency = (uiLocale?: string): string => {
  const language = (uiLocale || (typeof navigator !== 'undefined' && navigator.language)) || 'en-US';
  return currencyFromLocale(language);
};

export const SUPPORTED_CURRENCIES = Object.keys(CURRENCY_LOCALE_MAP);

/**
 * Maps ISO 3166-1 alpha-2 country codes and common English/Russian country names
 * to currency codes. Used by EventWizard to auto-suggest budget currency on location change.
 */
const COUNTRY_CURRENCY_MAP: Record<string, string> = {
  // Russia & CIS → RUB
  RU: 'RUB', RUSSIA: 'RUB', РОССИЯ: 'RUB',
  BY: 'RUB', BELARUS: 'RUB', БЕЛАРУСЬ: 'RUB',
  KZ: 'RUB', KAZAKHSTAN: 'RUB', КАЗАХСТАН: 'RUB',
  KG: 'RUB', KYRGYZSTAN: 'RUB', КЫРГЫЗСТАН: 'RUB',
  // United States → USD
  US: 'USD', 'UNITED STATES': 'USD', США: 'USD',
  // Eurozone → EUR
  DE: 'EUR', GERMANY: 'EUR', ГЕРМАНИЯ: 'EUR',
  FR: 'EUR', FRANCE: 'EUR', ФРАНЦИЯ: 'EUR',
  IT: 'EUR', ITALY: 'EUR', ИТАЛИЯ: 'EUR',
  ES: 'EUR', SPAIN: 'EUR', ИСПАНИЯ: 'EUR',
  NL: 'EUR', NETHERLANDS: 'EUR', НИДЕРЛАНДЫ: 'EUR',
  AT: 'EUR', AUSTRIA: 'EUR', АВСТРИЯ: 'EUR',
  PT: 'EUR', PORTUGAL: 'EUR', ПОРТУГАЛИЯ: 'EUR',
  FI: 'EUR', FINLAND: 'EUR', ФИНЛЯНДИЯ: 'EUR',
  IE: 'EUR', IRELAND: 'EUR', ИРЛАНДИЯ: 'EUR',
  GR: 'EUR', GREECE: 'EUR', ГРЕЦИЯ: 'EUR',
  BE: 'EUR', BELGIUM: 'EUR', БЕЛЬГИЯ: 'EUR',
  EE: 'EUR', ESTONIA: 'EUR', ЭСТОНИЯ: 'EUR',
  LV: 'EUR', LATVIA: 'EUR', ЛАТВИЯ: 'EUR',
  LT: 'EUR', LITHUANIA: 'EUR', ЛИТВА: 'EUR',
  UA: 'EUR', UKRAINE: 'EUR', УКРАИНА: 'EUR',
  SK: 'EUR', SLOVAKIA: 'EUR', СЛОВАКИЯ: 'EUR',
  SI: 'EUR', SLOVENIA: 'EUR', СЛОВЕНИЯ: 'EUR',
  HR: 'EUR', CROATIA: 'EUR', ХОРВАТИЯ: 'EUR',
  CY: 'EUR', CYPRUS: 'EUR', КИПР: 'EUR',
  MT: 'EUR', MALTA: 'EUR', МАЛЬТА: 'EUR',
  LU: 'EUR', LUXEMBOURG: 'EUR', ЛЮКСЕМБУРГ: 'EUR',
  // United Kingdom → GBP
  GB: 'GBP', UK: 'GBP', 'UNITED KINGDOM': 'GBP', ВЕЛИКОБРИТАНИЯ: 'GBP', АНГЛИЯ: 'GBP',
  // Switzerland → CHF
  CH: 'CHF', SWITZERLAND: 'CHF', ШВЕЙЦАРИЯ: 'CHF',
  // Japan → JPY
  JP: 'JPY', JAPAN: 'JPY', ЯПОНИЯ: 'JPY',
  // China → CNY
  CN: 'CNY', CHINA: 'CNY', КИТАЙ: 'CNY',
  // Canada → CAD
  CA: 'CAD', CANADA: 'CAD', КАНАДА: 'CAD',
  // Australia / New Zealand → AUD
  AU: 'AUD', AUSTRALIA: 'AUD', АВСТРАЛИЯ: 'AUD',
  NZ: 'AUD', 'NEW ZEALAND': 'AUD', 'НОВАЯ ЗЕЛАНДИЯ': 'AUD',
  // South Korea → KRW
  KR: 'KRW', 'SOUTH KOREA': 'KRW', КОРЕЯ: 'KRW', 'ЮЖНАЯ КОРЕЯ': 'KRW',
};

/**
 * Returns a currency code for the given country.
 * Accepts ISO 3166-1 alpha-2 codes ("RU", "DE") or common names ("Russia", "Германия").
 * Falls back to USD for unknown countries.
 */
export function getCurrencyByCountry(country: string): string {
  if (!country) return DEFAULT_CURRENCY;
  return COUNTRY_CURRENCY_MAP[country.trim().toUpperCase()] ?? DEFAULT_CURRENCY;
}

export function getLocaleByCurrency(currency?: string): string {
  if (!currency) {
    return DEFAULT_LOCALE;
  }
  const normalized = currency.toUpperCase();
  return CURRENCY_LOCALE_MAP[normalized] ?? DEFAULT_LOCALE;
}

/**
 * Formats a UC (internal coin) value with locale-aware number formatting.
 * Strips unnecessary trailing zeros (36.23, not 36.2300000000000004).
 */
export function formatUC(value: number, locale?: string): string {
  return new Intl.NumberFormat(locale ?? 'en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(value);
}

export function formatCurrency(
  value: number | null | undefined,
  currency?: string,
  options?: Intl.NumberFormatOptions,
): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const targetCurrency = currency?.toUpperCase() || DEFAULT_CURRENCY;
  const locale = getLocaleByCurrency(targetCurrency);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: targetCurrency,
    maximumFractionDigits: 2,
    ...(options ?? {}),
  }).format(value);
}

/**
 * Базовая валюта расчётов приложения — валюта, в которой Stripe списывает деньги
 * и в которой сервер отдаёт цены платформы (`monthlyPrice`, `priceEur`, комиссия
 * за публикацию).
 *
 * EUR по ADR-MONEY §7 (решение владельца 02.08.2026, отменяет прежнюю базу USD):
 * компания зарегистрирована в Финляндии, стартовый рынок — Франция.
 *
 * ГДЕ ЭТА КОНСТАНТА УЖЕ НЕ НУЖНА: контракт `GET /subscriptions/tiers` с EUR-6
 * отдаёт валюту явно (`tier.currency` ← `subscription_tiers.currency`) — цены
 * тиров подписываются ЕЮ, а не этой константой. Проверено живой пробой
 * 23.08.2026: у всех пяти платных тиров валюта и сумма в БД совпали с
 * `stripe.prices.retrieve` по их `stripePriceId`.
 *
 * ГДЕ ЕЩЁ НУЖНА: суммы, которые сервер по-прежнему отдаёт без валюты, —
 * AI-пакеты (`priceEur`), комиссия за публикацию события. Остаток мест
 * перечислен в EUR-5/EUR-7; по мере того как контракты начнут нести валюту,
 * область этой константы должна сокращаться, а не расти.
 */
export const BASE_CURRENCY = 'EUR';

/** Курс годен, только если он конечный и строго положительный (иначе — нет курса). */
function finiteRate(rate: number | undefined): number | null {
  if (rate == null || !Number.isFinite(rate) || rate <= 0) return null;
  return rate;
}

/**
 * Показать сумму, выраженную в базовой валюте (`BASE_CURRENCY`), в валюте локали
 * пользователя (display-only, ADR-EXCHANGE-RATES-conversion-service). НИКАКОЙ
 * фабрикации: нет валидного курса — возвращаем `null`, вызывающий показывает
 * исходную сумму в базовой валюте.
 *
 * База набора курсов приходит с сервера (`{ base, rates }`) и НЕ обязана совпадать
 * с нашей базовой валютой. С EUR-8 (23.08.2026) сервер публикует от EUR
 * (BE `ExchangeRatesService.BASE_CURRENCY`), и работает ветка прямого умножения —
 * ровно так, как эта функция и была написана: правки фронта разворот не потребовал.
 *
 * Ветка пересчёта через чужую базу (`amount(EUR) → base → target`) НЕ удалена. Она не
 * мёртвая: база объявляется сервером в рантайме, а не типом, и старая сборка фронта
 * может оказаться рядом с бэкендом любой из двух версий. Оба курса в этой ветке — из
 * одного авторитетного ответа (ЕЦБ/ЦБ СНГ/гос-пег), ничего не выдумывается.
 *
 * @param amount сумма в `BASE_CURRENCY`
 * @param toCurrency целевая валюта показа (ISO-4217)
 * @param rates карта курсов «единиц за 1 base» (из `GET /exchange-rates/latest`)
 * @param ratesBase база набора курсов, как её объявил сервер (поле `base`)
 * @returns сконвертированная сумма или `null`, если нужного курса нет
 */
export function convertFromBase(
  amount: number | null | undefined,
  toCurrency: string | undefined,
  rates: Record<string, number> | undefined,
  ratesBase: string | undefined,
): number | null {
  if (amount == null || !Number.isFinite(amount)) return null;
  const target = (toCurrency || '').toUpperCase();
  if (!target || target === BASE_CURRENCY) return amount; // EUR→EUR тождественно

  const base = (ratesBase || '').toUpperCase();
  if (!base) return null; // база набора неизвестна — трактовать курсы нечем

  // Курсы опубликованы от нашей базы — прямое умножение.
  if (base === BASE_CURRENCY) {
    const rate = finiteRate(rates?.[target]);
    return rate == null ? null : amount * rate;
  }

  // Курсы опубликованы от другой базы: сначала переводим сумму в неё.
  const baseToOurs = finiteRate(rates?.[BASE_CURRENCY]); // напр. USD→EUR
  if (baseToOurs == null) return null;
  const amountInRatesBase = amount / baseToOurs;
  if (target === base) return amountInRatesBase;

  const baseToTarget = finiteRate(rates?.[target]); // напр. USD→RUB
  if (baseToTarget == null) return null;
  return amountInRatesBase * baseToTarget;
}

/**
 * Символ валюты для полей ввода (`InputAdornment`), где полноформатный
 * `Intl.NumberFormat` неуместен. Нет символа у валюты — вернётся её код.
 */
export function getCurrencySymbol(currency: string = BASE_CURRENCY): string {
  const code = currency.toUpperCase();
  const parts = new Intl.NumberFormat(getLocaleByCurrency(code), {
    style: 'currency',
    currency: code,
    maximumFractionDigits: 0,
  }).formatToParts(0);
  return parts.find((p) => p.type === 'currency')?.value ?? code;
}

/**
 * Сумма в базовой валюте платформы (цена тира, комиссия за публикацию, AI-пакет).
 * Единая точка форматирования: раньше такие суммы собирались строкой `$${n}` и
 * расходились с фактическим списанием (ревью 22.08.2026, находка 3).
 */
export function formatBaseAmount(
  value: number | null | undefined,
  options?: Intl.NumberFormatOptions,
): string {
  return formatCurrency(value, BASE_CURRENCY, options);
}

/**
 * Итог по списку сумм, каждая из которых несёт СВОЮ валюту.
 *
 * Складывать 100 EUR и 100 RUB в одно число нельзя, а именно это делали
 * фронтовые `reduce((sum, e) => sum + e.amount, 0)` — и подписывали результат
 * одной валютой (EUR-5, EUR-18). Здесь суммы группируются по валюте, и каждая
 * группа форматируется своей: «1 200,00 € + 30 000,00 ₽». Одна валюта на входе —
 * одна сумма на выходе, то есть обычный случай выглядит как раньше.
 *
 * Строки без валюты относятся к `BASE_CURRENCY`: контракт её не назвал, а сумму
 * посчитал сервер в функциональной валюте платформы (ADR-MONEY §7).
 *
 * @param items суммы с валютами; пустой список даёт ноль в `BASE_CURRENCY`
 */
/**
 * Единственная валюта набора — или `null`, если валют несколько.
 *
 * EUR-18: фронтовые сводки (итог по счетам, оборот, столбиковые диаграммы)
 * подписывались валютой ПЕРВОГО элемента списка. Пока все элементы в одной
 * валюте, это случайно верно; стоит одному приехать в другой — подпись врёт,
 * а само сложенное число не значит ничего. Поэтому решение принимается явно:
 * одна валюта — считаем и подписываем как раньше, несколько — показываем
 * разбивку (`formatMixedTotal`) и не выводим производные величины (проценты
 * изменения, прогресс к цели, пороги статуса), которых при смешении не
 * существует.
 *
 * Элементы без валюты относятся к `fallback` (по умолчанию — базовая валюта
 * платформы, ADR-MONEY §7), а не к валюте зрителя.
 *
 * @param items элементы с необязательным полем валюты
 * @param fallback валюта для элементов, где поле не заполнено
 * @returns код валюты, если он один на весь набор; `null` при смешении.
 *          Пустой набор — это `fallback`, а не смешение.
 */
export function singleCurrencyOf(
  // `| undefined` объявлен явно из-за `exactOptionalPropertyTypes`: без него
  // объект, у которого поле валюты присвоено как `undefined` (а не опущено),
  // компилятор сюда не пустит — хотя для рантайма это один и тот же случай.
  items: ReadonlyArray<{ currency?: string | null | undefined }>,
  fallback: string = BASE_CURRENCY,
): string | null {
  const codes = new Set(
    items.map((item) => (item.currency || fallback).toUpperCase()),
  );
  if (codes.size > 1) return null;
  return [...codes][0] ?? fallback.toUpperCase();
}

export function formatMixedTotal(
  items: ReadonlyArray<{ amount: number | null | undefined; currency?: string | null | undefined }>,
  options?: Intl.NumberFormatOptions,
): string {
  const totals = new Map<string, number>();
  for (const item of items) {
    const amount = Number(item.amount ?? 0);
    if (!Number.isFinite(amount)) continue;
    const currency = (item.currency || BASE_CURRENCY).toUpperCase();
    totals.set(currency, (totals.get(currency) ?? 0) + amount);
  }
  if (totals.size === 0) return formatCurrency(0, BASE_CURRENCY, options);
  return [...totals.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, sum]) => formatCurrency(sum, currency, options))
    .join(' + ');
}
